<template>
  <div id="app" :class="{ 'dark-mode': isDarkMode }">
    <AppHeader v-if="showHeader" :is-dark-mode="isDarkMode" @toggle-dark-mode="toggleDarkMode" />
    <router-view />
  </div>
</template>

<script>
import './assets/css/index.css';
import AppHeader from './components/Header.vue';
import AuthService from './services/AuthService';
import { getCurrentUser, isAuthenticated, updateStoredUser } from './services/authStorage';
import { getApiErrorEventName, isApiException, notifyApiException } from './services/apiErrorToast';

export default {
  name: 'App',
  components: {
    AppHeader
  },
  data() {
    const currentUser = getCurrentUser();
    return {
      coinName: '',
      minMarketcap: null,
      maxMarketcap: null,
      sortOrder: 'asc',
      coins: [],
      currentPage: 1,
      itemsPerPage: 10,
      loading: false,
      error: null,
      isDarkMode: currentUser?.darkMode === true,
      darkModeRequestId: 0,
      authChangedHandler: null,
      apiErrorHandler: null,
      unhandledRejectionHandler: null
    };
  },
  mounted() {
    this.removeLegacyDarkModeSetting();
    this.applyDarkMode();
    this.loadDarkMode();
    this.authChangedHandler = () => {
      this.syncDarkModeFromStoredUser();
      this.loadDarkMode();
    };
    window.addEventListener('auth-changed', this.authChangedHandler);
    this.unhandledRejectionHandler = (event) => {
      if (!isApiException(event.reason)) {
        return;
      }
      event.preventDefault();
      notifyApiException(event.reason);
    };
    window.addEventListener('unhandledrejection', this.unhandledRejectionHandler);
    this.apiErrorHandler = (event) => {
      this.$toast.open({
        message: event.detail?.message || 'API request failed.',
        type: 'error',
        position: 'top-right'
      });
    };
    window.addEventListener(getApiErrorEventName(), this.apiErrorHandler);
  },
  beforeUnmount() {
    if (this.authChangedHandler) {
      window.removeEventListener('auth-changed', this.authChangedHandler);
    }
    if (this.apiErrorHandler) {
      window.removeEventListener(getApiErrorEventName(), this.apiErrorHandler);
    }
    if (this.unhandledRejectionHandler) {
      window.removeEventListener('unhandledrejection', this.unhandledRejectionHandler);
    }
  },
  methods: {
    async toggleDarkMode() {
      if (!isAuthenticated()) {
        return;
      }
      const previousDarkMode = this.isDarkMode;
      this.isDarkMode = !this.isDarkMode;
      this.applyDarkMode();
      const requestId = ++this.darkModeRequestId;
      try {
        const response = await AuthService.updateDarkMode(this.isDarkMode);
        if (requestId === this.darkModeRequestId) {
          this.setDarkMode(response.darkMode === true);
          this.syncStoredUserDarkMode(this.isDarkMode);
        }
      } catch (e) {
        if (requestId === this.darkModeRequestId) {
          this.setDarkMode(previousDarkMode);
        }
      }
    },
    async loadDarkMode() {
      if (!isAuthenticated()) {
        this.setDarkMode(false);
        return;
      }
      try {
        const response = await AuthService.getDarkMode();
        this.setDarkMode(response.darkMode === true);
        this.syncStoredUserDarkMode(this.isDarkMode);
      } catch (e) {
        this.syncDarkModeFromStoredUser();
      }
    },
    setDarkMode(darkMode) {
      this.isDarkMode = darkMode;
      this.applyDarkMode();
    },
    applyDarkMode() {
      if (this.isDarkMode) {
        document.body.classList.add('dark-mode');
      } else {
        document.body.classList.remove('dark-mode');
      }
    },
    syncDarkModeFromStoredUser() {
      this.setDarkMode(getCurrentUser()?.darkMode === true);
    },
    syncStoredUserDarkMode(darkMode) {
      const currentUser = getCurrentUser();
      if (currentUser && currentUser.darkMode !== darkMode) {
        updateStoredUser({ ...currentUser, darkMode });
      }
    },
    removeLegacyDarkModeSetting() {
      try {
        localStorage.removeItem('darkMode');
      } catch (e) {
        // localStorage might not be available
      }
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.coins.length / this.itemsPerPage);
    },
    paginatedCoins() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.coins.slice(start, end);
    },
    showHeader() {
      return !this.$route.meta.public;
    }
  },
};
</script>
